import { useState, type FormEvent } from 'react';
import { trpc } from '../api/trpc';
import { useDialogClose } from '../hooks/useDialogClose';
import { OktaWizard } from './OktaWizard';
import { MicrosoftWizard } from './MicrosoftWizard';
import { MicrosoftAutomatedInstall, isProvisioningInProgress } from './MicrosoftAutomatedInstall';
import { MicrosoftConnectorSettings } from './MicrosoftConnectorSettings';
import { AwsAutomatedInstall, isAwsProvisioningInProgress } from './AwsAutomatedInstall';
import { AwsConnectorSettings } from './AwsConnectorSettings';
import { ConnectorDeleteControl } from './ConnectorDeleteControl';
import { getAvailableTypes, getCatalogEntry } from '../connectors/catalog';

// The picker only offers connectors whose wizard is wired. Upcoming
// connectors (Coming-soon section in ConnectorList) flow through the
// notify-me path instead.
const PRODUCT_TYPES = getAvailableTypes();

interface Connector {
  id: string;
  productType: string;
  name: string;
  config: unknown;
}

interface Props {
  connector: Connector | null;
  preselectedType?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function ConnectorForm({ connector, preselectedType, onClose, onSaved }: Props) {
  const { isClosing, handleClose } = useDialogClose(onClose);
  const isEdit = connector !== null;
  const config = (connector?.config ?? {}) as Record<string, string>;

  const [productType, setProductType] = useState(connector?.productType ?? preselectedType ?? '');
  const [apiKey, setApiKey] = useState(config['apiKey'] ?? '');
  const [endpointUrl, setEndpointUrl] = useState(config['endpointUrl'] ?? '');
  const [skipTlsVerify, setSkipTlsVerify] = useState(config['skipTlsVerify'] === 'true' || config['skipTlsVerify'] === true as unknown as string);
  // Okta OAuth2 fields (used for edit mode)
  const [domain, setDomain] = useState(config['domain'] ?? '');
  const [clientId, setClientId] = useState(config['clientId'] ?? '');
  const [privateKeyJwk, setPrivateKeyJwk] = useState(config['privateKeyJwk'] ?? '');
  const [error, setError] = useState('');

  const isOkta = productType === 'okta';
  const isMicrosoft = productType === 'microsoft';
  const isAws = productType === 'aws';
  const [useManualMicrosoft, setUseManualMicrosoft] = useState(false);
  const [msProvisioningState, setMsProvisioningState] = useState('choose_permissions');
  const [awsProvisioningState, setAwsProvisioningState] = useState('tier_selection');
  // Edit-mode toggle: settings panel by default, wizard when the user clicks "Change tier".
  const [showAwsWizardOverride, setShowAwsWizardOverride] = useState(false);

  const createMut = trpc.connectors.create.useMutation();
  const updateMut = trpc.connectors.update.useMutation();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    const configPayload = isOkta
      ? {
          domain,
          clientId,
          privateKeyJwk,
          ...(skipTlsVerify ? { skipTlsVerify: true } : {}),
        }
      : {
          apiKey,
          ...(endpointUrl ? { endpointUrl } : {}),
          ...(skipTlsVerify ? { skipTlsVerify: true } : {}),
        };

    try {
      if (isEdit) {
        await updateMut.mutateAsync({
          connectorId: connector.id,
          config: configPayload,
        });
      } else {
        await createMut.mutateAsync({
          productType,
          name: getCatalogEntry(productType)?.label || productType,
          config: configPayload,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save connector');
    }
  }

  async function handleOktaWizardSubmit(wizardConfig: Record<string, unknown>) {
    setError('');
    try {
      await createMut.mutateAsync({
        productType: 'okta',
        name: 'Okta',
        config: wizardConfig,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save connector');
    }
  }

  async function handleMicrosoftWizardSubmit(wizardConfig: Record<string, unknown>) {
    setError('');
    try {
      await createMut.mutateAsync({
        productType: 'microsoft',
        name: 'Microsoft',
        config: wizardConfig,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save connector');
    }
  }

  // Okta creation: 4-step wizard
  if (isOkta && !isEdit) {
    return (
      <div className={`modal-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={handleClose}>
        <div className={`modal-content wizard${isClosing ? ' dialog-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
          <h3 className="modal-title">Connect Okta</h3>
          <OktaWizard
            onSubmit={(config) => { void handleOktaWizardSubmit(config); }}
            isPending={createMut.isPending}
            error={error}
          />
        </div>
      </div>
    );
  }

  // Microsoft edit: show connector settings with app details and permissions
  if (isMicrosoft && isEdit) {
    return (
      <div className={`modal-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={handleClose}>
        <div className={`modal-content wizard${isClosing ? ' dialog-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
          <h3 className="modal-title">Microsoft Connector Settings</h3>
          <MicrosoftConnectorSettings
            connector={{ id: connector.id, name: connector.name, config: connector.config as Record<string, unknown> }}
            onClose={handleClose}
            onSaved={onSaved}
          />
        </div>
      </div>
    );
  }

  // Microsoft creation: automated 2-click flow (default) or legacy 4-step wizard (fallback)
  if (isMicrosoft && !isEdit) {
    if (!useManualMicrosoft) {
      const inProgress = isProvisioningInProgress(msProvisioningState as never);
      return (
        <div className={`modal-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={inProgress ? undefined : handleClose}>
          <div className={`modal-content wizard${isClosing ? ' dialog-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Connect Microsoft Security</h3>
            <MicrosoftAutomatedInstall
              onComplete={onSaved}
              onFallbackToManual={() => setUseManualMicrosoft(true)}
              onStateChange={setMsProvisioningState}
            />
          </div>
        </div>
      );
    }
    return (
      <div className={`modal-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={handleClose}>
        <div className={`modal-content wizard${isClosing ? ' dialog-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
          <h3 className="modal-title">Connect Microsoft Security (Manual)</h3>
          <MicrosoftWizard
            onSubmit={(config) => { void handleMicrosoftWizardSubmit(config); }}
            isPending={createMut.isPending}
            error={error}
          />
        </div>
      </div>
    );
  }

  // AWS: edit-mode shows the settings panel (tier + connected accounts +
  // test/change-tier actions, mirroring MicrosoftConnectorSettings); first-
  // time install runs the wizard. The wizard is reused for tier changes,
  // since the backend upserts on (tenantId, awsAccountId).
  if (isAws && isEdit && !showAwsWizardOverride) {
    return (
      <div className={`modal-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={handleClose}>
        <div className={`modal-content wizard${isClosing ? ' dialog-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
          <h3 className="modal-title">AWS Connector Settings</h3>
          <AwsConnectorSettings
            connector={{ id: connector!.id, name: connector!.name, config: connector!.config as Record<string, unknown> }}
            onClose={handleClose}
            onChangeTier={() => setShowAwsWizardOverride(true)}
            onSaved={onSaved}
          />
        </div>
      </div>
    );
  }

  if (isAws) {
    const inProgress = isAwsProvisioningInProgress(awsProvisioningState as never);
    return (
      <div className={`modal-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={inProgress ? undefined : handleClose}>
        <div className={`modal-content wizard${isClosing ? ' dialog-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
          <h3 className="modal-title">{isEdit ? 'Change AWS tier' : 'Connect AWS'}</h3>
          <AwsAutomatedInstall
            onComplete={onSaved}
            onStateChange={setAwsProvisioningState}
          />
        </div>
      </div>
    );
  }

  // Generic create/edit form (Splunk and any other API-key/endpoint connector).
  // When launched from a specific connector card, `preselectedType` is set and
  // the product is already chosen, so the product-type picker is redundant
  // (req 64: "selecting a product opens a form with required fields"). Show the
  // picker only for the generic "Add connector" entry (create, no preselection);
  // keep the disabled-locked indicator on edit. Title the dialog after the
  // product when it's already known, e.g. "Splunk connector".
  const cardLaunched = !isEdit && Boolean(preselectedType);
  const showTypePicker = isEdit ? !isOkta : !preselectedType;
  const productLabel = getCatalogEntry(productType)?.label ?? productType;
  const genericTitle = isEdit
    ? isOkta
      ? 'Edit Okta Connector'
      : 'Edit Connector'
    : cardLaunched
      ? `${productLabel} connector`
      : 'Add Connector';

  return (
    <div className={`modal-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content${isClosing ? ' dialog-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-title">{genericTitle}</h3>
        <form onSubmit={(e) => { void handleSubmit(e); }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {showTypePicker && (
            <div className="form-field">
              <label htmlFor="conn-type">Product type</label>
              <select
                id="conn-type"
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                disabled={isEdit}
                required
              >
                <option value="" disabled>Select a product...</option>
                {PRODUCT_TYPES.map((p) => (
                  <option key={p.type} value={p.type}>{p.label}</option>
                ))}
              </select>
            </div>
          )}
          {isOkta && isEdit ? (
            <>
              <div className="form-field">
                <label htmlFor="conn-domain">Okta Domain</label>
                <input
                  id="conn-domain"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="your-org.okta.com"
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="conn-client-id">Client ID</label>
                <input
                  id="conn-client-id"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="0oa..."
                  required
                  autoComplete="off"
                />
              </div>
              <div className="form-field">
                <label htmlFor="conn-private-key">Private Key (JWK JSON)</label>
                <textarea
                  id="conn-private-key"
                  value={privateKeyJwk}
                  onChange={(e) => setPrivateKeyJwk(e.target.value)}
                  placeholder='{"kty":"RSA","n":"...","e":"...","d":"..."}'
                  required
                  rows={4}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                />
              </div>
            </>
          ) : !isOkta ? (
            <>
              <div className="form-field">
                <label htmlFor="conn-key">API Key / Token</label>
                <input
                  id="conn-key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter API key or Bearer token"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="form-field">
                <label htmlFor="conn-url">Endpoint URL</label>
                <input
                  id="conn-url"
                  type="url"
                  value={endpointUrl}
                  onChange={(e) => setEndpointUrl(e.target.value)}
                  placeholder="https://host:port"
                />
              </div>
            </>
          ) : null}
          {!isOkta && (
            <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input
                id="conn-tls"
                type="checkbox"
                checked={skipTlsVerify}
                onChange={(e) => setSkipTlsVerify(e.target.checked)}
                style={{ width: 'auto' }}
              />
              <label htmlFor="conn-tls" style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
                Skip TLS certificate validation (for self-signed certs)
              </label>
            </div>
          )}
          {error && <p className="login-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={handleClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={createMut.isPending || updateMut.isPending}>
              {isEdit ? 'Save changes' : 'Connect'}
            </button>
          </div>
        </form>
        {connector && (
          <ConnectorDeleteControl
            connectorId={connector.id}
            connectorLabel={connector.name}
            onDeleted={onSaved}
          />
        )}
      </div>
    </div>
  );
}
